import { create } from 'zustand';
import type { DashboardData, UserActivity } from '../types/dashboard';
import { dashboardService } from '../services/dashboardService';
import { logger } from '../utils/logger';


interface DashboardState {
  // Data
  dashboardData: DashboardData | null;
  userActivities: UserActivity[];
  
  // Loading states
  isLoading: boolean;
  isActivitiesLoading: boolean;
  
  // Error states
  error: string | null;
  activitiesError: string | null;
  
  // Actions
  fetchDashboardData: (userId?: string) => Promise<void>;
  fetchUserActivities: (limit?: number) => Promise<void>;
  trackActivity: (action: string, entityType: string, entityId: string, metadata?: string) => Promise<void>;
  clearError: () => void;
  clearActivitiesError: () => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  // Initial state
  dashboardData: null,
  userActivities: [],
  isLoading: false,
  isActivitiesLoading: false,
  error: null,
  activitiesError: null,

  // Actions
  fetchDashboardData: async (userId?: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const data = await dashboardService.getDashboardData(userId);
      set({ dashboardData: data, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard data';
      set({ error: errorMessage, isLoading: false });
    }
  },

  fetchUserActivities: async (limit = 50) => {
    set({ isActivitiesLoading: true, activitiesError: null });
    
    try {
      const activities = await dashboardService.getUserActivities(limit);
      set({ userActivities: activities, isActivitiesLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user activities';
      set({ activitiesError: errorMessage, isActivitiesLoading: false });
    }
  },

  trackActivity: async (action: string, entityType: string, entityId: string, metadata?: string) => {
    try {
      await dashboardService.trackActivity({
        action,
        entityType,
        entityId,
        metadata
      });
    } catch (error) {
      logger.error('Failed to track activity:', error);
    }
  },

  clearError: () => set({ error: null }),
  clearActivitiesError: () => set({ activitiesError: null })
}));
